import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Star, ArrowLeft, Play, RotateCcw } from "lucide-react";

interface Challenge {
  key: string;
  name: string;
  emoji: string;
  instruction: string;
  targetCount: number;
  durationSeconds: number;
}

const CHALLENGES: Challenge[] = [
  { key: "jumping", name: "تحدي القفز", emoji: "🦘", instruction: "اقفز 10 مرات! اضغط الزر في كل قفزة", targetCount: 10, durationSeconds: 30 },
  { key: "breathing", name: "تمرين التنفس", emoji: "🌬️", instruction: "تنفس بعمق... شهيق... زفير... كرر 5 مرات", targetCount: 5, durationSeconds: 40 },
  { key: "stretching", name: "تمرين التمدد", emoji: "🧘", instruction: "مدّ يديك للأعلى ثم للأسفل! كرر 8 مرات", targetCount: 8, durationSeconds: 30 },
  { key: "clapping", name: "تحدي التصفيق", emoji: "👏", instruction: "صفق بيديك 15 مرة بسرعة!", targetCount: 15, durationSeconds: 20 },
  { key: "running", name: "الجري في المكان", emoji: "🏃", instruction: "اجرِ في مكانك! اضغط الزر مع كل خطوة - 20 خطوة!", targetCount: 20, durationSeconds: 30 },
  { key: "balance", name: "تمرين التوازن", emoji: "🦩", instruction: "قف على رجل واحدة! اضغط الزر كل ثانية تثبت فيها - 10 ثواني!", targetCount: 10, durationSeconds: 20 },
  { key: "toes", name: "لمس أصابع القدم", emoji: "🤸", instruction: "انحنِ والمس أصابع قدميك! كرر 8 مرات", targetCount: 8, durationSeconds: 25 },
  { key: "squats", name: "تمرين القرفصاء", emoji: "🏋️", instruction: "انزل وقف! كرر تمرين القرفصاء 10 مرات", targetCount: 10, durationSeconds: 30 },
];

interface Props {
  onComplete: (score: number, stars: number, activityKey: string) => void;
  onBack: () => void;
}

export default function HealthActivity({ onComplete, onBack }: Props) {
  const [challengeIdx, setChallengeIdx] = useState(0);
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [finished, setFinished] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const challenge = CHALLENGES[challengeIdx];

  useEffect(() => {
    if (!started || finished) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setFinished(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [started, finished]);

  const startChallenge = () => {
    setStarted(true);
    setCount(0);
    setFinished(false);
    setTimeLeft(challenge.durationSeconds);
  };

  const handleTap = () => {
    if (!started || finished) return;
    const newCount = count + 1;
    setCount(newCount);
    if (newCount >= challenge.targetCount) {
      clearInterval(timerRef.current);
      setFinished(true);
      const score = 10;
      setTotalScore(prev => prev + score);
    }
  };

  const handleNext = () => {
    if (!finished) {
      const pct = count / challenge.targetCount;
      setTotalScore(prev => prev + Math.round(pct * 10));
    }
    if (challengeIdx < CHALLENGES.length - 1) {
      setChallengeIdx(prev => prev + 1);
      setStarted(false);
      setCount(0);
      setFinished(false);
    } else {
      setAllDone(true);
      const maxScore = CHALLENGES.length * 10;
      const finalScore = totalScore + (finished ? 0 : Math.round((count / challenge.targetCount) * 10));
      const pct = (finalScore / maxScore) * 100;
      const stars = pct >= 80 ? 3 : pct >= 50 ? 2 : 1;
      onComplete(finalScore, stars, "health_challenges");
    }
  };

  if (allDone) {
    const maxScore = CHALLENGES.length * 10;
    const pct = (totalScore / maxScore) * 100;
    const stars = pct >= 80 ? 3 : pct >= 50 ? 2 : 1;
    return (
      <Card className="text-center border-2 border-green-300 bg-gradient-to-b from-green-50 to-white">
        <CardContent className="p-8 space-y-4">
          <div className="text-6xl">💪</div>
          <h2 className="text-2xl font-bold text-foreground">أحسنت يا بطل!</h2>
          <div className="flex justify-center gap-1">
            {[1, 2, 3].map(s => (
              <Star key={s} className={`h-10 w-10 ${s <= stars ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
            ))}
          </div>
          <p className="text-lg text-muted-foreground">حصلت على {totalScore} من {maxScore} نقطة</p>
          <Button onClick={onBack} variant="outline">
            <ArrowLeft className="h-4 w-4 ml-2" /> العودة للأنشطة
          </Button>
        </CardContent>
      </Card>
    );
  }

  const progress = (count / challenge.targetCount) * 100;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button onClick={onBack} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        <Badge variant="secondary">{challengeIdx + 1} / {CHALLENGES.length}</Badge>
      </div>

      <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
        <CardHeader className="text-center">
          <div className="text-5xl mb-2">{challenge.emoji}</div>
          <CardTitle className="text-xl">{challenge.name}</CardTitle>
          <p className="text-muted-foreground">{challenge.instruction}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {!started ? (
            <Button onClick={startChallenge} className="w-full" size="lg">
              <Play className="h-5 w-5 ml-2" /> ابدأ التحدي!
            </Button>
          ) : (
            <>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>⏱️ {timeLeft} ثانية</span>
                <span>{count} / {challenge.targetCount}</span>
              </div>
              <Progress value={Math.min(progress, 100)} className="h-4" />

              {!finished ? (
                <Button onClick={handleTap} size="lg" className="w-full h-24 text-2xl">
                  {challenge.emoji} اضغط هنا! ({count})
                </Button>
              ) : (
                <div className="text-center space-y-3">
                  {count >= challenge.targetCount ? (
                    <p className="text-xl font-bold text-green-600">🎉 أحسنت! أتممت التحدي!</p>
                  ) : (
                    <p className="text-xl font-bold text-orange-500">⏰ انتهى الوقت! أنجزت {count} من {challenge.targetCount}</p>
                  )}
                  <Button onClick={handleNext} className="w-full" size="lg">
                    {challengeIdx < CHALLENGES.length - 1 ? "التحدي التالي ➡️" : "عرض النتيجة 💪"}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
